import type { ColumnDef, Row } from '@tanstack/react-table';
import { SortableHeader } from '../shared/SortableHeader';
import type { Scope } from '../../api/types/permissions';
import type { TenantUserListItemLocal, UserLevelScopeLocal } from '../../api/types/TenantLocal';

/**
 * Lo ÚNICO que la tabla necesita saber de un nivel: su key y cómo se llama.
 *
 * Tipo propio (no `TenantLevelLocal`, que se indexa del openapi generado) para que el motor de la
 * tabla no dependa del shape del wire: los dos orígenes —`TenantDetail` de platform y `/me/profile`—
 * lo satisfacen estructuralmente, y el día que uno cambie el wire, la tabla no se entera.
 */
export interface TenantLevelOption {
    level: Scope;
    displayName: string;
}

/**
 * Columnas POR NIVEL de la tabla de usuarios (una por nivel que define la aplicación del tenant:
 * "Grupos" en agents, "Sucursales"/"Tiendas" en otro) + el filtro que las acompaña.
 *
 * Por qué una columna por nivel y NO "agrupar por": un usuario puede estar en VARIOS destinos del
 * mismo nivel (3 roles sobre 3 grupos). Un group-by con multi-valor obliga a repetir la fila en cada
 * bucket y los conteos dejan de cerrar. Filtrar por destino responde lo mismo ("quiénes están en el
 * Grupo 1") sin duplicados, y ordenar por la columna junta visualmente a los del mismo destino.
 */

/** Sentinel del filtro "sin destino en este nivel" (un admin TENANT no está en ningún grupo). */
export const NO_LEVEL_VALUE = '__none__';

/** Texto de un destino sin nombre resuelto: nunca se muestra el scopeRef. */
export const UNNAMED_LEVEL_SCOPE = 'Sin nombre';

/** Muestra el nombre que manda el back; «Sin nombre» solo si no viene. */
export function levelScopeLabel(scope: UserLevelScopeLocal): string {
    const name = scope.displayName?.trim();
    return name ? name : UNNAMED_LEVEL_SCOPE;
}

/** Ordena alfabéticamente en español, sin distinguir mayúsculas ni acentos. */
function compareLabels(
    a: Row<TenantUserListItemLocal>,
    b: Row<TenantUserListItemLocal>,
    id: string,
) {
    return String(a.getValue(id)).localeCompare(String(b.getValue(id)), 'es', {
        sensitivity: 'base',
    });
}

/**
 * Los destinos de UN nivel dentro de la fila, en el orden que vino del backend.
 * `levelScopes` SIEMPRE está materializado (`normalizeTenantUser` lo garantiza, incluso contra un
 * backend que no manda el campo) → aquí no hace falta defensa extra.
 */
export function scopesOfLevel(user: TenantUserListItemLocal, level: Scope): UserLevelScopeLocal[] {
    return user.levelScopes.filter((s) => s.level === level);
}

/**
 * Opciones del <select> de filtro de un nivel, derivadas de las FILAS CARGADAS (no de un fetch del
 * catálogo): el list del backend trae todos los usuarios manejables de una, así que los destinos
 * presentes son exactamente los que pueden dar resultados. Un destino sin usuarios no aparece —
 * filtrar por él devolvería la tabla vacía igual. Dedupe por scopeRef, orden alfabético por etiqueta.
 */
export function levelFilterOptions(
    users: TenantUserListItemLocal[],
    level: Scope,
): Array<{ value: string; label: string }> {
    const byRef = new Map<string, string>();
    for (const user of users) {
        for (const scope of scopesOfLevel(user, level)) {
            if (!byRef.has(scope.scopeRef)) byRef.set(scope.scopeRef, levelScopeLabel(scope));
        }
    }
    return [...byRef.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

/**
 * `filterFn` de una columna de nivel. El valor del filtro es un `scopeRef` (coincide si el usuario
 * tiene ESE destino) o el sentinel `NO_LEVEL_VALUE` (usuarios sin ningún destino en el nivel).
 */
export function levelColumnFilterFn(
    row: Row<TenantUserListItemLocal>,
    level: Scope,
    filterValue: string,
): boolean {
    const scopes = scopesOfLevel(row.original, level);
    if (filterValue === NO_LEVEL_VALUE) return scopes.length === 0;
    return scopes.some((s) => s.scopeRef === filterValue);
}

/**
 * Columna de un nivel. El `accessorFn` devuelve las etiquetas unidas por coma: da ORDEN alfabético
 * (junta a los del mismo destino) y entra al buscador global sin trabajo extra. El filtro exacto no
 * usa ese string sino los `scopeRef` de la fila (ver `levelColumnFilterFn`).
 */
export function buildLevelColumn(level: TenantLevelOption): ColumnDef<TenantUserListItemLocal> {
    return {
        id: `level:${level.level}`,
        accessorFn: (row) =>
            scopesOfLevel(row, level.level).map(levelScopeLabel).join(', ') || undefined,
        // Los usuarios sin destino quedan al final en los dos sentidos.
        sortUndefined: 'last',
        sortingFn: compareLabels,
        header: ({ column }) => <SortableHeader column={column} title={level.displayName} />,
        filterFn: (row, _id, value) => levelColumnFilterFn(row, level.level, value as string),
        cell: (info) => {
            const scopes = scopesOfLevel(info.row.original, level.level);
            if (scopes.length === 0) {
                // Sin destino en este nivel. El caso típico NO es un error: un admin con assignment
                // TENANT alcanza a todo el tenant, no "pertenece" a un grupo.
                return <span className="text-muted-foreground">—</span>;
            }
            return (
                <div className="flex flex-wrap justify-center gap-1">
                    {scopes.map((s) => (
                        <span
                            key={s.scopeRef}
                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                        >
                            {levelScopeLabel(s)}
                        </span>
                    ))}
                </div>
            );
        },
    };
}
